// SPDX-License-Identifier: LicenseRef-MMS-WIRE
// Copyright (c) 2016-2026 Metal Management Solutions (Pty) Ltd

import {
    AfterViewInit,
    Component,
    DestroyRef,
    ElementRef,
    OnInit,
    Renderer2,
    ViewChild,
    ViewEncapsulation,
    inject,
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ApiService} from "../../services/api/api.service";
import {AppScope} from '../../services/app_scope.service';
import * as utils from '../../lib/utils';
import {merge, Observable, of, Subject} from "rxjs";
import {catchError, debounceTime, first, map, switchMap, tap} from "rxjs/operators";
import {ScrollableDirective} from '../../directives/mention.directive';
import {SearchQueryOptions} from "../../services/api/search-query-options";
import {getContainsFilter} from "../../services/api/filter_utils";
import {SelectSearchApiService} from "../select-search-api/select-search-api.service";
import {User} from "../../_models/users";
import {MatActionList, MatListItem} from "@angular/material/list";
import {DivInfiniteScrollDirective} from "../../directives/div-infinite-scroll.directive";
import {CommonModule} from "@angular/common";

@Component({
    selector: 'at-who',
    templateUrl: './at-who.component.html',
    styleUrls: ['./at-who.component.scss'],
    encapsulation: ViewEncapsulation.None,
    imports: [
        MatActionList,
        ScrollableDirective,
        DivInfiniteScrollDirective,
        CommonModule,
        MatListItem
    ],
    providers: [SelectSearchApiService]
})
export class AtWhoComponent implements OnInit, AfterViewInit {
    users: User[];
    filtered_users: User[];
    searchQuery: string = '';
    list_index: number = 0;

    options$: Observable<any[]>;
    search_string = '';
    @ViewChild('user_list') user_list: ElementRef;
    @ViewChild(ScrollableDirective) list: ScrollableDirective;

    private readonly destroyRef = inject(DestroyRef);
    private readonly searchText$ = new Subject<string>();
    private readonly nextPage$ = new Subject<void>();
    private currentRequestId = 0;

    constructor(private api: ApiService,
                public appScope: AppScope,
                private renderer: Renderer2,
                public sapi: SelectSearchApiService,
    ) {

        this.options$ = this.sapi.connect(this.mapFunction);
        this.options$.pipe(
            tap(list => {
                this.filtered_users = list;
            }),
            takeUntilDestroyed(this.destroyRef),
        ).subscribe();
    }

    mapFunction(curr) {
        return curr;
    };

    ngOnInit() {
        this.appScope.mentionEmit.pipe(
            takeUntilDestroyed(this.destroyRef),
        ).subscribe(string => {
            this.doSearch(string);
        });

        // Synchronous state for each typed query happens in doSearch() so that
        // Enter/Tab before the debounce fires sees the current searchQuery.
        // Both new searches and paged loads go through one switchMap so a fresh
        // query cancels any in-flight page-of-old-query response.
        const newSearch$ = this.searchText$.pipe(
            debounceTime(200),
            map(query => ({query, page: 1, requestId: this.currentRequestId})),
        );
        const page$ = this.nextPage$.pipe(
            map(() => ({
                query: this.searchQuery,
                page: this.sapi.page_number,
                requestId: this.currentRequestId,
            })),
        );

        merge(newSearch$, page$).pipe(
            switchMap(({query, page, requestId}) =>
                this.fetchData(page, query).pipe(
                    map(response => ({response, query, requestId, error: false})),
                    catchError(() => of({response: null, query, requestId, error: true})),
                ),
            ),
            takeUntilDestroyed(this.destroyRef),
        ).subscribe(({response, query, requestId, error}) => {
            if (requestId !== this.currentRequestId) {
                return;
            }
            this.sapi.loadingAvailable = false;
            if (error) {
                this.sapi.loadNext = false;
                return;
            }
            if (!response) {
                return;
            }
            if (query !== this.searchQuery) {
                return;
            }
            this.sapi.page_number += 1;
            this.sapi.options_available.next(response.data);
            if (!response.data.length) {
                this.sapi.loadNext = false;
            }
            this.sapi.availableTotal = response.meta?.count || 0;
        });
    }

    ngAfterViewInit() {
        this.list.scrollTop = 0;
    }

    doSearch(string) {
        const ctrl = this;

        if (string === "select") { //Enter or tab
            // Typing synchronously clears filtered_users (via options_available.next([]) +
            // scan's clear branch), so this guard covers the "before debounced search returns"
            // case without also blocking selection during pagination, where filtered_users
            // still holds the already-loaded entries while sapi.loadingAvailable is true.
            if (!this.filtered_users?.[this.list_index]) {
                return;
            }
            this.addWho(this.filtered_users[this.list_index]);
        } else if (string === "ArrowUp") {
            if (this.list_index === 0) {
                this.list_index = this.filtered_users.length - 1;
            } else {
                this.list_index -= 1;
            }
            if (this.list_index === this.filtered_users.length - 1) {
                this.list.scrollTop = (this.filtered_users.length - 4) * 48;
            } else {
                this.list.scrollTop = (this.list_index - 3) * 48
            }
        } else if (string === "ArrowDown") {
            if (this.list_index === this.filtered_users.length - 1) {
                this.list_index = 0;
            } else {
                this.list_index += 1;
            }
            if (this.list_index > 3) {
                this.list.scrollTop = (this.list_index - 3) * 48;
            } else {
                this.list.scrollTop = 0;
            }
        } else if (string === "") { //reset
            this.searchQuery = "";
            this.list_index = 0;
            this.filtered_users = utils.deepCopy(ctrl.users);
            // Cancel any in-flight request from the previous mention session and
            // clear the spinner so the next time the panel opens it starts clean.
            this.currentRequestId++;
            this.sapi.loadingAvailable = false;
        } else {
            const query = string.substr(1, string.length);
            this.searchQuery = query;
            this.list_index = 0;
            this.sapi.page_number = 1;
            this.sapi.clear = true;
            this.sapi.options_available.next([]);
            this.sapi.loadNext = true;
            this.sapi.loadingAvailable = true;
            this.currentRequestId++;
            this.searchText$.next(query);
        }
    }

    getNextBatch() {
        if (!this.sapi.loadNext || this.sapi.loadingAvailable) {
            return;
        }
        this.sapi.loadingAvailable = true;
        this.nextPage$.next();
    }

    fetchData = (page_number, filterValue): Observable<any> => {
        const ctrl = this;
        const options = new SearchQueryOptions();
        options.page_number = page_number
        options.page_size = this.sapi.page_size;
        options.sort = 'name';
        let filters = [];
        if (filterValue) {
            filters.push({
                or: ['username', 'name', 'alias', 'last_name']
                    .map(attribute => getContainsFilter(attribute, filterValue))
            });
        }
        if (filters.length) {
            options.filters = filters;
        }
        return ctrl.api.users.searchMany(options).pipe(
            first(),
            map(response => {
                if (!response) {
                    return of([]);
                }
                ctrl.users = response.data;
                return response;
            }),
        );
    }

    addWho(user) {
        this.appScope.mentionSelected.next({
            user: user,
            search_string: this.searchQuery,
            index: this.appScope.at_who_index
        });
    }

    updateCurrent(user) {
        this.list_index = this.filtered_users.indexOf(user);
    }
}
